import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ShowcasePiece } from '../../data/showcasePieces';

interface ShowcaseModalProps {
  piece: Exclude<ShowcasePiece, { mediaType: 'longform' | 'link' }>;
  onClose: () => void;
}

type VideoEmbed = {
  youtubeId?: string;
  vimeoId?: string;
  youtubeStart?: number;
};

const buildEmbedUrl = (v: VideoEmbed, autoplay: boolean): string => {
  const auto = autoplay ? 'autoplay=1&' : '';
  if (v.vimeoId) {
    return `https://player.vimeo.com/video/${v.vimeoId}?${auto}title=0&byline=0&portrait=0`;
  }
  if (v.youtubeId) {
    const start = v.youtubeStart ? `&start=${v.youtubeStart}` : '';
    return `https://www.youtube.com/embed/${v.youtubeId}?${auto}rel=0&modestbranding=1${start}`;
  }
  return '';
};

const embedKey = (v: VideoEmbed): string => v.vimeoId || v.youtubeId || '';

const ShowcaseModal: React.FC<ShowcaseModalProps> = ({ piece, onClose }) => {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);

  const galleryLength =
    piece.mediaType === 'gallery' ? piece.items.length : 1;

  const goPrev = useCallback(() => {
    if (piece.mediaType !== 'gallery') return;
    setIndex((i) => (i - 1 + galleryLength) % galleryLength);
  }, [piece.mediaType, galleryLength]);

  const goNext = useCallback(() => {
    if (piece.mediaType !== 'gallery') return;
    setIndex((i) => (i + 1) % galleryLength);
  }, [piece.mediaType, galleryLength]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        goPrev();
      } else if (e.key === 'ArrowRight') {
        goNext();
      }
    };
    document.addEventListener('keydown', handleKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, goPrev, goNext]);

  const handleBackdrop = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleRelatedClick = () => {
    if (!piece.related) return;
    onClose();
    navigate(`/${piece.related.slug}`);
  };

  const renderMedia = () => {
    if (piece.mediaType === 'image') {
      return (
        <figure className="showcase-modal-figure">
          <img src={piece.fullImage} alt={piece.title} />
          {piece.caption && <figcaption>{piece.caption}</figcaption>}
        </figure>
      );
    }
    if (piece.mediaType === 'video') {
      if (piece.youtubeId || piece.vimeoId) {
        return (
          <figure className="showcase-modal-figure">
            <div className="showcase-modal-video">
              <iframe
                src={buildEmbedUrl(piece, true)}
                title={piece.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                allowFullScreen
              />
            </div>
            {piece.caption && <figcaption>{piece.caption}</figcaption>}
          </figure>
        );
      }
      return (
        <figure className="showcase-modal-figure">
          <video
            src={piece.videoSrc}
            poster={piece.poster}
            controls
            autoPlay
            playsInline
          />
          {piece.caption && <figcaption>{piece.caption}</figcaption>}
        </figure>
      );
    }
    const current = piece.items[index];
    const isVideoItem = current.type === 'video';
    return (
      <figure className="showcase-modal-figure">
        <div
          className={`showcase-modal-gallery ${
            isVideoItem ? 'is-video-item' : ''
          }`}
        >
          {galleryLength > 1 && (
            <button
              className="showcase-modal-nav prev"
              onClick={goPrev}
              aria-label="Previous"
            >
              ‹
            </button>
          )}
          {isVideoItem && current.type === 'video' ? (
            // Re-key the iframe so navigating away from a playing video
            // unmounts the player rather than leaving it running behind
            // the new item.
            <div className="showcase-modal-video" key={embedKey(current)}>
              <iframe
                src={buildEmbedUrl(current, false)}
                title={`${piece.title} — ${index + 1} of ${galleryLength}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                allowFullScreen
              />
            </div>
          ) : current.type === 'image' ? (
            <img
              src={current.src}
              alt={`${piece.title} ${index + 1} of ${galleryLength}`}
            />
          ) : null}
          {galleryLength > 1 && (
            <button
              className="showcase-modal-nav next"
              onClick={goNext}
              aria-label="Next"
            >
              ›
            </button>
          )}
        </div>
        <figcaption>
          {current.caption && <span>{current.caption}</span>}
          {galleryLength > 1 && (
            <span className="showcase-modal-counter">
              {index + 1} / {galleryLength}
            </span>
          )}
        </figcaption>
      </figure>
    );
  };

  // Portaled to document.body so the modal escapes the showcase pane's
  // stacking context (the pane has z-index: 2 for its directional
  // shadow, which would otherwise trap the modal below the deck
  // chevrons).
  return createPortal(
    <div
      className="showcase-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={piece.title}
      onClick={handleBackdrop}
    >
      <div className="showcase-modal">
        <button
          className="showcase-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <header className="showcase-modal-header">
          <p className="showcase-modal-context">{piece.context}</p>
          <h2 className="showcase-modal-title">{piece.title}</h2>
        </header>
        {renderMedia()}
        {piece.related && (
          <footer className="showcase-modal-footer">
            <button
              className="showcase-modal-related"
              onClick={handleRelatedClick}
            >
              {piece.related.hookCopy}
            </button>
          </footer>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ShowcaseModal;
